import { Injectable } from '@angular/core';

import { environment } from '../../environments/environment';

declare const mixpanel;

export interface MixpanelConfig {
  /** super properties cookie expiration (in days) */
  cookie_expiration?: number;

  /** super properties span subdomains */
  cross_subdomain_cookie?: boolean;

  /** debug mode */
  debug?: boolean;

  /**
   * if this is true, the mixpanel cookie or localStorage entry will be deleted,
   * and no user persistence will take place
   */
  disable_persistence?: boolean;

  /**
   * if this is true, Mixpanel will automatically determine City, Region, and
   * Country data using the IP address of the client
   */
  ip?: boolean;

  /**
   * type of persistent store for super properties (cookie/* localStorage) if set
   * to 'localStorage', any existing mixpanel cookie value with the same persistence_name
   * will be transferred to localStorage and deleted
   */
  persistence?: 'cookie' | 'localStorage';

  /** name for super properties persistent store */
  persistence_name?: string;

  /** names of properties/superproperties which should never be sent with track() calls */
  property_blacklist?: string[];

  /**
   * if this is true, mixpanel cookies will be marked as secure, meaning they will only
   * be transmitted over https
   */
  secure_cookie?: boolean;

  /** the amount of time track_links will wait for Mixpanel's servers to respond */
  track_links_timeout?: number;

  /** should we track a page view on page load */
  track_pageview?: boolean;

  /**
   * if you set upgrade to be true, the library will check for a cookie from our
   * old js library and import super properties from it, then the old cookie is deleted
   * The upgrade config option only works in the initialization, so make sure you
   * set it when you create the library.
   */
  upgrade?: boolean;
}

@Injectable()
export class MixpanelService {
  static readonly EventNamePageView = 'Page view';
  static readonly EventNameAccountActivation = 'Account activation';
  static readonly EventNameAccountLogin = 'Account login';
  static readonly EventNameSubmitHomeInspectionReimbursement = 'Submit Home Inspection reimbursement';
  static readonly EventNameSubmitServiceGuaranteeReimbursement = 'Submit Service Guarantee reimbursement';
  static readonly EventNameSubmitCashBackReimbursement = 'Submit Cash Back reimbursement';

  static readonly PropertyNamePageTitle = 'Title';
  static readonly PropertyNameFirstLoginDateTimeUTC = 'First Login Date-Time UTC';
  static readonly PropertyNameLastLoginDateTimeUTC = 'Last Login Date-Time UTC';
  static readonly PropertyNameInitialMarketingOptIn = 'Initial Marketing Consent';
  static readonly PropertyNameSubscriberFirstName = 'first_name';
  static readonly PropertyNameSubscriberLastName = 'last_name';
  static readonly PropertyNameSubscriberPhoneNumber = '$phone';
  static readonly PropertyNameSubscriberEmailAddress = '$email';
  static readonly PropertyNameSubscriberProductPlan = 'Product Plan';
  static readonly PropertyNameClaimedReimbursementAmount = 'Claimed Amount';

  static readonly DefaultConfig: MixpanelConfig = {
    cookie_expiration: 365,
    cross_subdomain_cookie: true,
    debug: false,
    disable_persistence: false,
    ip: true,
    persistence: 'cookie',
    persistence_name: '',
    property_blacklist: [],
    secure_cookie: false,
    track_links_timeout: 300,
    track_pageview: true,
    upgrade: false,
  };

  // Project token to use for tracking analytic events
  readonly projectToken: string | null = typeof environment.mixpanelProjectToken === 'string' &&
    mixpanel && typeof mixpanel.init === 'function' ? environment.mixpanelProjectToken : null;

  // Mixpanel-generated unique ID of the visitor
  private _distinctId: string | null = null;

  private static debugOnlyLog(e): void {
    if (!environment.production) {
      console.log(e);
    }
  }

  private static tryInvoke(invokeFn: () => void, catchErrorFn?: (error: any) => void) {
    try {
      invokeFn();
    } catch (e) {
      if (typeof catchErrorFn === 'function') {
        catchErrorFn(e);
      } else {
        MixpanelService.debugOnlyLog(e);
      }
    }
  }

  constructor() {
    const success = this.init();
    if (!environment.production && !success && console && console.warn) {
      console.warn('Mixpanel initialization failed.');
    }
  }

  /**
   * @see https://mixpanel.com/help/reference/javascript-full-api-reference#mixpanel.init
   *
   * Once successfully initialized, repeated calls to this method will be ignored.
   */
  init(): boolean {
    if (this._distinctId) {
      return true;
    } else if (this.projectToken) {
      // ASSUMPTION: mixpanel.init is a synchronous call
      try {
        mixpanel.init(this.projectToken, {
          loaded: (mp): void => {
            this._distinctId = mp.get_distinct_id();
            mp.set_config({
              //debug: !environment.production,
              secure_cookie: environment.production,
              track_pageview: false
            });
          }
        });
        return true;
      } catch (e) {
        MixpanelService.debugOnlyLog(e);
      }
    }
    return false;
  }

  /**
   * Gets the mixpanel-generated distinct ID of the visitor.
   */
  get distinctId(): string | null {
    return this._distinctId;
  }

  // helper method which invokes the specified mixpanel function within a try-catch block
  // and logs any errors to console only if current environment is non-production
  private tryInvoke(mixpanelFn: () => void, catchErrorFn?: (error: any) => void) {
    if (this.init()) {
      MixpanelService.tryInvoke(mixpanelFn, catchErrorFn);
    }
  }

  /**
   * @see https://mixpanel.com/help/reference/javascript-full-api-reference#mixpanel.set_config
   */
  setConfig(config: MixpanelConfig): void {
    const newConfig = Object.assign({}, MixpanelService.DefaultConfig, config);
    this.tryInvoke(() => mixpanel.set_config(newConfig));
  }

  /**
   * @see https://mixpanel.com/help/reference/javascript-full-api-reference#mixpanel.alias
   */
  alias(subscriberId: string, originalId?: string): void {
    this.tryInvoke(() => mixpanel.alias(subscriberId, originalId));
  }

  /**
   * @see https://mixpanel.com/help/reference/javascript-full-api-reference#mixpanel.identify
   */
  identify(subscriberId: string): void {
    this.tryInvoke(() => mixpanel.identify(subscriberId));
  }

  /**
   * @see https://mixpanel.com/help/reference/javascript-full-api-reference#mixpanel.time_event
   */
  timeEvent(eventName: string): void {
    this.tryInvoke(() => mixpanel.time_event(eventName));
  }

  /**
   * @see https://mixpanel.com/help/reference/javascript-full-api-reference#mixpanel.track
   */
  track(eventName: string, properties?: { [key: string]: string }, callback?: () => void): void {
    this.tryInvoke(() => mixpanel.track(eventName, properties, callback));
  }

  /**
   * Equivalent to calling: track(MixpanelService.EventNamePageView, { Title: <<page_title>> }, ...)
   */
  trackPageView(pageTitle?: string, properties?: { [key: string]: string }, callback?: () => void): void {
    const titleProp = typeof pageTitle === 'string' && { [MixpanelService.PropertyNamePageTitle]: pageTitle };
    const props = Object.assign({}, titleProp, properties);
    this.track(MixpanelService.EventNamePageView, props, callback);
  }

  setSuperProperty(props: { [key: string]: string }, options?: { once: boolean; }): void {
    if (!Object.keys(props).length) { return; }
    this.tryInvoke(options && options.once
      ? () => mixpanel.register_once(props)
      : () => mixpanel.register(props));
  }

  setPeopleProperty(props: { [key: string]: string }, options?: { once: boolean; }): void {
    if (!Object.keys(props).length) { return; }
    this.tryInvoke(options && options.once
      ? () => mixpanel.people.set_once(props)
      : () => mixpanel.people.set(props));
  }
}



// WEBPACK FOOTER //
// ./src/app/_services/mixpanel.service.ts